import React, {useCallback} from 'react';

import {BulkActionDownload, BulkActionResultModal} from 'src/components/bulk-tool';
import {CompleteMark} from 'src/components/complete-mark';
import {UserLink} from 'src/components/links';
import {RowNumbered} from 'src/components/row-numbered';
import {useUserStatuses, useUserTypes} from 'src/hooks/dictionaries';
import {getUserStatesBulkUpdateProgress, getUserStatesBulkUpdateResult} from 'src/openapi/admin/apis/UserStateBulkUpdateApi';
import {useAppDispatch} from 'src/store/hooks';

import {Group} from '@pb/kit-component-group';
import {Body, Cell, Head, Row, Table} from '@pb/kit-component-table';

import {FILE_HEADERS, FILE_NAME, TOOL_TITLE} from './constants';

import type {Pagination} from 'src/openapi/admin/models/Pagination';
import type {UserStateProcessedItem} from 'src/openapi/admin/models/UserStateProcessedItem';

type Props = {
    id: string,
    onClose: () => void,
};

export const UpdateUserStateResultModal = ({id, onClose}: Props) => {
    const apiDispatch = useAppDispatch();

    const {getUserStatusByCode} = useUserStatuses();
    const {getUserTypeByCode} = useUserTypes();

    const getPaginatedResults = useCallback(
        (bulkResult: UserStateProcessedItem[], {currentPage, pageSize}: Pagination) => bulkResult
            .slice((currentPage - 1) * pageSize, currentPage * pageSize)
            .map((res: UserStateProcessedItem, index) => (
                <Row key={index}>
                    <Cell width={50}>
                        <RowNumbered
                            maxRowNumber={bulkResult?.length || 0}
                            placeholder={(currentPage - 1) * pageSize + index + 1}
                        />
                    </Cell>
                    <Cell>
                        <UserLink
                            userId={res.userId || 0}
                            caption={res.userEmail}
                            blank={true}
                            place={`Bulk ${TOOL_TITLE} Result`}
                        />
                    </Cell>
                    <Cell>
                        {getUserTypeByCode(res.oldType)?.description} ({
                            getUserStatusByCode(res.oldStatus)?.description
                        })
                    </Cell>
                    <Cell>
                        {getUserTypeByCode(res.newType)?.description} ({
                            getUserStatusByCode(res.newStatus)?.description
                        })
                    </Cell>
                    <Cell><CompleteMark errors={res.errors}/></Cell>
                </Row>
            )),
        [getUserStatusByCode, getUserTypeByCode],
    );

    return (
        <BulkActionResultModal<UserStateProcessedItem>
            id={id}
            onClose={onClose}

            toolTitle={TOOL_TITLE}
            description={bulkResult => (
                <Group justify="space-between" alignItems="center">
                    <b>Update results</b>
                    <BulkActionDownload
                        toolName={TOOL_TITLE}
                        fileName={FILE_NAME}
                        headers={FILE_HEADERS}
                        startTime={bulkResult.startTime}
                        body={bulkResult.itemList?.map(item => ({
                            ...item,
                            userId: item.userId.toString(),
                            oldType: getUserTypeByCode(item.oldType)?.description,
                            newType: getUserTypeByCode(item.newType)?.description,
                            oldStatus: getUserStatusByCode(item.oldStatus)?.description,
                            newStatus: getUserStatusByCode(item.newStatus)?.description,
                            errors: item.errors?.join('. '),
                        })) as Record<string, string>[]}
                    />
                </Group>
            )}
            table={(bulkResult, pagination) => (
                <Table borderedGrid={true}>
                    <Head>
                        <Cell width={50}><strong>#</strong></Cell>
                        <Cell width={246}>User Email</Cell>
                        <Cell width={200}>Old State</Cell>
                        <Cell width={200}>New State</Cell>
                        <Cell width={150}>Action Status</Cell>
                    </Head>
                    <Body>
                        {getPaginatedResults(bulkResult, pagination)}
                    </Body>
                </Table>
            )}

            getProgress={() => apiDispatch(getUserStatesBulkUpdateProgress({id}))}
            getResult={() => apiDispatch(getUserStatesBulkUpdateResult({id}))}

            sortComparator={(a, b) => a.userEmail.localeCompare(b.userEmail)}
        />
    );
};
